import React, { useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { format } from 'date-fns';
import { Icon, Typography } from '@packages/ui/shared';
import { LoadingCircle } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';
import placeHolderImage from '../../assets/asset-placeholder@3x.png';

const useStyles = createUseStyles({
    container: {
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 4,
        height: 96,
    },
    image: {
        borderRadius: 4,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    imageContainer: {
        borderRadius: 4,
        height: 64,
        width: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        backgroundColor: colors.grayTwo,
        opacity: 0.8,
    },
    loadingOverlay: {
        background: colors.grayOne,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
    },
    leftWrapper: {
        flex: 1,
        minWidth: 0,
    },
    infoContainer: {
        height: 64,
        flex: 1,
        minWidth: 0,
        marginRight: 16,
    },
    progressTrack: {
        width: '100%',
        height: 8,
        flexShrink: 1,
        borderRadius: 4,
        background: '#D9D9D9',
        overflow: 'hidden',
        position: 'relative',
        margin: [4, 0],
    },
    progressIndicator: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '0%',
        background: 'var(--Brand-Main, #1F518F)',
        borderRadius: [8, 4, 4, 8],
        transition: 'width 400ms ease',
    },
});

interface ImagePreviewInfoProps {
    fileName: string;
    isSuccessful?: boolean;
    previewUrl: string;
    uploadedAt: string | null;
    uploadedBy: string;
    inProgress?: boolean;
}

export function ImagePreviewInfo({
    fileName,
    isSuccessful,
    previewUrl,
    uploadedAt,
    uploadedBy,
    inProgress = false,
}: ImagePreviewInfoProps) {
    const classes = useStyles();
    const altText = fileName ? fileName.replace(/\.[^/.]+$/, '') : 'Preview of uploaded image';
    const [isImageLoading, setImageLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [showBar, setShowBar] = useState(false);
    const intervalRef = useRef<number | null>(null);
    const hasFallbackImageRef = useRef(false);

    function handleImageError(e: React.SyntheticEvent<HTMLImageElement>) {
        if (hasFallbackImageRef.current) {
            return;
        }
        hasFallbackImageRef.current = true;
        e.currentTarget.src = placeHolderImage;
    }

    useEffect(() => {
        if (inProgress) {
            setShowBar(true);
            setProgress(0);
            if (intervalRef.current == null) {
                intervalRef.current = window.setInterval(() => {
                    setProgress((p) => {
                        const next = p + Math.random() * 3 + 1;
                        return Math.min(next, 90);
                    });
                }, 200);
            }
        }
        return () => {};
    }, [inProgress]);

    useEffect(() => {
        return () => {
            if (intervalRef.current != null) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const shouldComplete = !inProgress && showBar;
        if (!shouldComplete) return;

        if (intervalRef.current != null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        const raf = window.requestAnimationFrame(() => setProgress(100));

        const timeout = window.setTimeout(() => {
            setShowBar(false);
            setProgress(0);
        }, 600);

        return () => {
            window.cancelAnimationFrame(raf);
            clearTimeout(timeout);
        };
    }, [inProgress, showBar]);

    return (
        <div className={`d-flex justify-content-between align-items-center p-4 mt-3 ${classes.container}`}>
            <div className={`d-flex flex-row align-items-center ${classes.leftWrapper}`}>
                <div className={classes.imageContainer}>
                    {isImageLoading && (
                        <div className={classes.loadingOverlay}>
                            <LoadingCircle size={32} />
                        </div>
                    )}
                    <img
                        src={previewUrl ?? placeHolderImage}
                        alt={altText}
                        className={classes.image}
                        onLoad={() => setImageLoading(false)}
                        onError={handleImageError}
                        style={isImageLoading ? { opacity: 0 } : { opacity: 1, transition: 'opacity 0.2s' }}
                    />
                </div>
                <div className={`d-flex flex-column ml-3 justify-content-evenly ${classes.infoContainer}`}>
                    <Typography variant="label">{fileName}</Typography>
                    {showBar && (
                        <div className={classes.progressTrack}>
                            <div className={classes.progressIndicator} style={{ width: `${progress}%` }} />
                        </div>
                    )}
                    <Typography variant="caption" color="textDarkSecondary">
                        {uploadedAt ? format(uploadedAt, 'PPpp') : '--'} &nbsp;|&nbsp; {uploadedBy}
                    </Typography>
                </div>
            </div>
            {typeof isSuccessful === 'boolean' && (
                <Icon
                    name={inProgress ? 'x-circle' : isSuccessful ? 'check-circle-hollow' : 'x-circle'}
                    color={inProgress ? 'brandSecondary' : isSuccessful ? 'greenOne' : 'redOne'}
                    data-testid="status-icon"
                />
            )}
        </div>
    );
}
